<template>
  <div class="ai-chat">
    <div v-if="!isInternalUser" class="ai-chat__denied">
      Dieser Bereich ist nur für interne Accounts verfügbar.
    </div>

    <template v-else>
      <!-- Session view -->
      <div v-if="sessionId" class="ai-chat__session">
        <div class="ai-chat__session-header">
          <button type="button" class="ai-chat__back" @click="goHome">← Chats</button>
          <h2 class="ai-chat__title">{{ sessionTitle || 'Chat' }}</h2>
        </div>

        <div v-if="loading" class="ai-chat__status">Lade Chat…</div>
        <div v-else-if="error" class="ai-chat__error">{{ error }}</div>

        <div ref="thread" class="ai-chat__thread">
          <div
            v-for="msg in messages"
            :key="msg.message_id"
            :class="['ai-chat__turn', `ai-chat__turn--${msg.role}`]"
          >
            <div :class="['ai-chat__bubble', `ai-chat__bubble--${msg.role}`]">
              <div class="ai-chat__bubble-text">{{ msg.content }}</div>
            </div>
            <div
              v-if="msg.role === 'assistant' && (msg.referenced_items || []).length"
              class="ai-chat__refs"
            >
              <AiChatContentCard
                v-for="(item, idx) in msg.referenced_items"
                :key="msg.message_id + '-' + idx"
                :item="item"
              />
            </div>
          </div>

          <div v-if="busy" class="ai-chat__turn ai-chat__turn--assistant" role="status" aria-live="polite">
            <div class="ai-chat__bubble ai-chat__bubble--assistant ai-chat__bubble--pending">
              <span class="ai-chat__dots" aria-hidden="true">
                <span /><span /><span />
              </span>
              <span>Assistent denkt nach …</span>
            </div>
          </div>
        </div>

        <form class="ai-chat__composer" @submit.prevent="sendFollowUp">
          <input
            v-model="input"
            type="text"
            class="ai-chat__input"
            :placeholder="busy ? 'Warte auf Antwort …' : 'Nachfrage stellen…'"
            :disabled="busy"
            enterkeyhint="send"
          />
          <button type="submit" class="ai-chat__send" :disabled="busy || !input.trim()" aria-label="Senden">
            <span v-if="busy" class="ai-chat__spinner" aria-hidden="true" />
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M3.4 20.4l17.45-7.48a1 1 0 000-1.84L3.4 3.6a.993.993 0 00-1.39.91L2 9.12a1 1 0 00.89.98L17 12 2.89 13.9a1 1 0 00-.89.98l.01 4.61c0 .78.84 1.26 1.39.91z" fill="currentColor"/>
            </svg>
          </button>
        </form>
      </div>

      <!-- Home / list view -->
      <div v-else class="ai-chat__home">
        <h2 class="ai-chat__title">FitPlus Chat</h2>
        <p class="ai-chat__subtitle">Fragen zu Workouts, Artikeln und deinem Training.</p>

        <form class="ai-chat__composer" @submit.prevent="startChat">
          <input
            v-model="message"
            type="text"
            class="ai-chat__input"
            placeholder="z.B. Kurzes Core-Workout ohne Geräte?"
            :disabled="busy"
            enterkeyhint="send"
          />
          <button type="submit" class="ai-chat__send" :disabled="busy || !message.trim()" aria-label="Chat starten">
            <span v-if="busy" class="ai-chat__spinner" aria-hidden="true" />
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M3.4 20.4l17.45-7.48a1 1 0 000-1.84L3.4 3.6a.993.993 0 00-1.39.91L2 9.12a1 1 0 00.89.98L17 12 2.89 13.9a1 1 0 00-.89.98l.01 4.61c0 .78.84 1.26 1.39.91z" fill="currentColor"/>
            </svg>
          </button>
        </form>

        <div
          v-if="busy"
          class="ai-chat__pending"
          role="status"
          aria-live="polite"
        >
          <span class="ai-chat__spinner ai-chat__spinner--dark" aria-hidden="true" />
          <span>Assistent wird vorbereitet …</span>
        </div>

        <div v-if="error" class="ai-chat__error">{{ error }}</div>

        <div v-if="sessions.length" class="ai-chat__list">
          <h3 class="ai-chat__list-title">Letzte Chats</h3>
          <button
            v-for="s in sessions"
            :key="s.sessionId"
            type="button"
            class="ai-chat__list-item"
            @click="openSession(s.sessionId)"
          >
            <span class="ai-chat__list-item-title">{{ s.title }}</span>
            <span class="ai-chat__list-item-preview">{{ s.lastMessagePreview }}</span>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import AiChatContentCard from '@/components/AiChatContentCard.vue';
import {
  createAiChatSession,
  getAiChatSession,
  listLocalAiChatSessions,
  rememberLocalAiChatSession,
  resolveAiChatSessionTitle,
  sendAiChatMessage,
} from '@/services/aiChatApi';
import { isSportplatzMediaEmail } from '@/utils/internalUser';

export default {
  name: 'AiChat',
  components: { AiChatContentCard },
  data() {
    return {
      message: '',
      input: '',
      sessions: [],
      messages: [],
      sessionTitle: '',
      loading: false,
      busy: false,
      error: '',
      autostartDone: false,
    };
  },
  computed: {
    sessionId() {
      return this.$route.params.sessionId || '';
    },
    user() {
      return this.$store.state.user && this.$store.state.user.user
        ? this.$store.state.user.user
        : null;
    },
    userId() {
      return this.user && this.user.uid ? this.user.uid : null;
    },
    isInternalUser() {
      const email = this.user && this.user.email ? this.user.email : '';
      return isSportplatzMediaEmail(email);
    },
  },
  watch: {
    sessionId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.loadSession();
        } else {
          this.sessions = listLocalAiChatSessions();
          this.maybeAutostart();
        }
      },
    },
    '$route.query.q'() {
      if (!this.sessionId) {
        this.maybeAutostart();
      }
    },
  },
  methods: {
    goHome() {
      this.$router.push({ name: 'AiChat' });
    },
    openSession(id) {
      this.$router.push({ name: 'AiChatSession', params: { sessionId: id } });
    },
    maybeAutostart() {
      if (this.autostartDone || this.busy || !this.isInternalUser) return;
      const q = this.$route.query.q;
      if (typeof q === 'string' && q.trim()) {
        this.autostartDone = true;
        this.message = q.trim();
        this.startChat();
      }
    },
    async startChat() {
      const text = (this.message || '').trim();
      if (!text || this.busy) return;
      this.busy = true;
      this.error = '';
      try {
        const created = await createAiChatSession('de');
        const sessionId = created.session_id;
        const response = await sendAiChatMessage(sessionId, text, { userId: this.userId });
        const title =
          resolveAiChatSessionTitle({
            sessionTitle: response.session_title,
            referencedItems: response.referenced_items,
            userMessage: text,
          }) || 'Neuer Chat';
        rememberLocalAiChatSession({
          sessionId,
          title,
          lastMessagePreview: text,
        });
        this.message = '';
        this.$router.replace({ name: 'AiChatSession', params: { sessionId } });
      } catch (e) {
        this.error = e instanceof Error ? e.message : 'Chat konnte nicht gestartet werden.';
      } finally {
        this.busy = false;
      }
    },
    async loadSession() {
      if (!this.sessionId) return;
      this.loading = true;
      this.error = '';
      this.messages = [];
      try {
        const session = await getAiChatSession(this.sessionId);
        this.sessionTitle = session.title || 'Chat';
        this.messages = (session.messages || []).filter((m) => m.role === 'user' || m.role === 'assistant');
        this.$nextTick(() => this.scrollThread());
      } catch (e) {
        this.error = e instanceof Error ? e.message : 'Chat konnte nicht geladen werden.';
      } finally {
        this.loading = false;
      }
    },
    async sendFollowUp() {
      const text = (this.input || '').trim();
      if (!text || this.busy || !this.sessionId) return;
      this.busy = true;
      this.error = '';
      const optimisticId = `local-${Date.now()}`;
      this.messages.push({
        message_id: optimisticId,
        role: 'user',
        content: text,
        referenced_items: [],
      });
      this.input = '';
      this.$nextTick(() => this.scrollThread());
      try {
        const response = await sendAiChatMessage(this.sessionId, text, { userId: this.userId });
        this.messages.push({
          message_id: response.message_id,
          role: 'assistant',
          content: response.content,
          referenced_items: response.referenced_items || [],
        });
        const title =
          resolveAiChatSessionTitle({
            sessionTitle: response.session_title,
            sessionResponseTitle: this.sessionTitle,
            referencedItems: response.referenced_items,
            userMessage: text,
          }) || this.sessionTitle;
        this.sessionTitle = title;
        rememberLocalAiChatSession({
          sessionId: this.sessionId,
          title,
          lastMessagePreview: text,
        });
        this.$nextTick(() => this.scrollThread());
      } catch (e) {
        this.error = e instanceof Error ? e.message : 'Nachricht konnte nicht gesendet werden.';
      } finally {
        this.busy = false;
        this.$nextTick(() => this.scrollThread());
      }
    },
    scrollThread() {
      const el = this.$refs.thread;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-chat {
  max-width: 640px;
  margin: 0 auto;
  padding: 16px 16px 96px;
  min-height: 60vh;
  display: flex;
  flex-direction: column;

  &__denied,
  &__status,
  &__error,
  &__subtitle {
    font-size: 14px;
    line-height: 1.4;
  }

  &__error {
    color: #b00020;
    margin: 12px 0;
  }

  &__title {
    font-size: 22px;
    font-weight: 700;
    margin: 0 0 8px;
  }

  &__subtitle {
    opacity: 0.7;
    margin: 0 0 16px;
  }

  &__session-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
  }

  &__back {
    border: 0;
    background: transparent;
    font-size: 14px;
    padding: 0;
    cursor: pointer;
  }

  &__thread {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-bottom: 12px;
    max-height: calc(100vh - 220px);
  }

  &__bubble {
    max-width: 92%;
    padding: 12px 14px;
    border-radius: 16px;
    font-size: 15px;
    line-height: 1.45;
    white-space: pre-wrap;

    &--user {
      align-self: flex-end;
      background: #1c2b36;
      color: #fff;
      border-bottom-right-radius: 4px;
    }

    &--assistant {
      align-self: flex-start;
      background: #f3f5f7;
      border-bottom-left-radius: 4px;
    }

    &--pending {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      color: rgba(0, 0, 0, 0.55);
      font-size: 14px;
    }
  }

  &__dots {
    display: inline-flex;
    gap: 4px;
    align-items: center;

    span {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.28);
      animation: ai-chat-bounce 1s infinite ease-in-out;

      &:nth-child(1) { animation-delay: -0.2s; }
      &:nth-child(2) { animation-delay: -0.1s; }
    }
  }

  &__pending {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 12px;
    background: #f3f5f7;
    font-size: 14px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.55);
  }

  &__spinner {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #fff;
    animation: ai-chat-spin 0.7s linear infinite;

    &--dark {
      border-color: rgba(0, 0, 0, 0.12);
      border-top-color: $colorPrimary;
    }
  }

  &__turn {
    display: flex;
    flex-direction: column;
    max-width: 100%;

    &--user {
      align-items: flex-end;
    }

    &--assistant {
      align-items: flex-start;
    }
  }

  &__refs {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: min(100%, 420px);
    margin-top: 8px;
    align-self: stretch;
  }

  &__composer {
    display: flex;
    gap: 8px;
    margin-top: 12px;
    align-items: center;
    width: 100%;
  }

  &__input {
    flex: 1 1 auto;
    min-width: 0;
    width: 100%;
    min-height: 44px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 12px;
    padding: 10px 14px;
    font-size: 15px;
    background: #fff;
    box-sizing: border-box;
  }

  &__send {
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    border: 0;
    border-radius: 12px;
    padding: 0;
    margin: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: $colorPrimary;
    color: #fff;
    cursor: pointer;
    box-shadow: 0 4px 12px 0 rgba(248, 121, 24, 0.24);

    &:disabled {
      opacity: 0.4;
      cursor: default;
      box-shadow: none;
    }
  }

  &__list {
    margin-top: 28px;
  }

  &__list-title {
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 10px;
    opacity: 0.7;
  }

  &__list-item {
    width: 100%;
    text-align: left;
    border: 0;
    background: #f3f5f7;
    border-radius: 12px;
    padding: 12px 14px;
    margin-bottom: 8px;
    cursor: pointer;

    &-title {
      display: block;
      font-weight: 600;
      font-size: 15px;
      margin-bottom: 2px;
    }

    &-preview {
      display: block;
      font-size: 13px;
      opacity: 0.65;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

@keyframes ai-chat-spin {
  to { transform: rotate(360deg); }
}

@keyframes ai-chat-bounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.35; }
  40% { transform: translateY(-3px); opacity: 1; }
}
</style>
